import type { DayKey, StreakState } from "./types";

/**
 * Generic per-day streaks (domain-agnostic). An "active" day counts; a gap
 * breaks the streak unless a freeze is spent ("freeze"). Pure.
 *
 * Derived from the habit-streak core without coupling to its model: here there
 * is only a set of active days + today + freezes.
 */

const MS_PER_DAY = 86_400_000;

/** `YYYY-MM-DD` (UTC) from epoch ms. */
export function dayKeyUTC(at: number): DayKey {
  return new Date(at).toISOString().slice(0, 10);
}

export function addDays(day: DayKey, n: number): DayKey {
  const t = Date.parse(`${day}T00:00:00.000Z`) + n * MS_PER_DAY;
  return new Date(t).toISOString().slice(0, 10);
}

/** Whole days between two keys (b - a). */
export function diffDays(a: DayKey, b: DayKey): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / MS_PER_DAY);
}

export interface StreakOpts {
  /** Freezes available to cover one-day gaps. */
  freezes?: number;
}

/**
 * Computes the streak (current + best) from the active days and `today`.
 * `today` without activity does NOT break it (it remains pending). Each gap
 * consumes a freeze (1 per day) while any remain; once exhausted, it breaks.
 */
export function computeStreak(
  activeDays: Iterable<DayKey>,
  today: DayKey,
  opts: StreakOpts = {},
): StreakState {
  const days = [...new Set(activeDays)].filter((d) => d <= today).toSorted();
  if (days.length === 0) {
    return { current: 0, best: 0 };
  }

  // Best: traverse in order while counting consecutive runs.
  let best = 1;
  let run = 1;
  for (let i = 1; i < days.length; i += 1) {
    const gap = diffDays(days[i - 1]!, days[i]!);
    run = gap === 1 ? run + 1 : 1;
    if (run > best) {
      best = run;
    }
  }

  // Current streak: count backward from the last active day, tolerating gaps
  // with freezes. It is anchored to today or yesterday (if today is not yet marked).
  const set = new Set(days);
  const last = days.at(-1)!;
  const sinceLast = diffDays(last, today);
  let freezes = opts.freezes ?? 0;
  // If the last active day is more than (1 + freezes) ago, the streak has died.
  if (sinceLast > 1 + freezes) {
    return { current: 0, best };
  }
  freezes -= Math.max(0, sinceLast - 1); // gap today <-> last consumes freezes

  let current = 0;
  let cursor: DayKey = last;
  while (true) {
    if (set.has(cursor)) {
      current += 1;
      cursor = addDays(cursor, -1);
      continue;
    }
    // Gap: try to cover it with a freeze.
    if (freezes > 0) {
      freezes -= 1;
      cursor = addDays(cursor, -1);
      continue;
    }
    break;
  }
  return { current, best: Math.max(best, current) };
}
